import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Link,
  Collapse,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Receipt,
  CreditCard,
  OpenInNew,
  GetApp,
  AccessTime,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
  Settings,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { format } from 'date-fns';
import ServiceTierSelect from './ServiceTierSelect';

// Format price from cents to euros with currency symbol
const formatPrice = (amount, currency = 'EUR') => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(amount / 100);
};

// Format timestamp to human-readable date
const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  return format(new Date(timestamp * 1000), 'dd MMM yyyy');
};

// Get status color based on invoice status
const getStatusColor = (status) => {
  switch (status) {
    case 'paid': return 'success';
    case 'open': return 'warning';
    case 'draft': return 'info';
    case 'uncollectible':
    case 'void':
      return 'error';
    default: return 'default';
  }
};

// Get status label based on invoice status
const getStatusLabel = (status) => {
  switch (status) {
    case 'paid': return 'Paid';
    case 'open': return 'Pending';
    case 'draft': return 'Draft';
    case 'uncollectible': return 'Uncollectible';
    case 'void': return 'Void';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

// Invoice list item component
const InvoiceListItem = ({ invoice, isMobile }) => {
  return (
    <>
      <ListItem
        alignItems="flex-start"
        sx={{ 
          flexDirection: isMobile ? 'column' : 'row',
          py: isMobile ? 2 : 1
        }}
        secondaryAction={
          !isMobile && (
            <Stack direction="row" spacing={1}>
              {invoice.url && (
                <Tooltip title="View Invoice">
                  <IconButton
                    edge="end"
                    size="small"
                    component="a"
                    href={invoice.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNew />
                  </IconButton>
                </Tooltip>
              )}
              {invoice.pdf && (
                <Tooltip title="Download PDF">
                  <IconButton
                    edge="end"
                    size="small"
                    component="a"
                    href={invoice.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GetApp />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )
        }
      >
        <ListItemText
          sx={{ width: '100%' }}
          primary={
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 1,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1 : 0
            }}>
              <Typography variant={isMobile ? "body1" : "subtitle1"}>
                Invoice #{invoice.number || invoice.id.substr(0, 8)}
              </Typography>
              <Chip
                size="small"
                label={getStatusLabel(invoice.status)}
                color={getStatusColor(invoice.status)}
              />
            </Box>
          }
          secondary={
            <Stack spacing={1}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 0.5 : 0
              }}>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(invoice.created)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: isMobile ? 'medium' : 'regular' }}>
                  {formatPrice(invoice.amount_due, invoice.currency)}
                </Typography>
              </Box>
              {invoice.description && (
                <Typography variant="body2" color="text.secondary">
                  {invoice.description}
                </Typography>
              )}
              {isMobile && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {invoice.url && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNew />}
                      component="a"
                      href={invoice.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </Button>
                  )}
                  {invoice.pdf && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<GetApp />}
                      component="a"
                      href={invoice.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          }
        />
      </ListItem>
      <Divider component="li" />
    </>
  );
};

// Payment method card component
const PaymentMethodCard = ({ paymentMethod, isMobile }) => {
  const { brand, last4, exp_month, exp_year } = paymentMethod.card || {};

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
        <Stack 
          direction={isMobile ? "column" : "row"} 
          spacing={isMobile ? 1 : 2} 
          alignItems={isMobile ? "flex-start" : "center"}
        >
          <CreditCard color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant={isMobile ? "body1" : "subtitle1"} gutterBottom>
              {brand} •••• {last4}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expires {exp_month}/{exp_year}
            </Typography>
          </Box>
          {paymentMethod.id === paymentMethod.default && (
            <Chip 
              label="Default" 
              color="primary" 
              size="small" 
              sx={{ 
                ml: isMobile ? 0 : 'auto',
                mt: isMobile ? 1 : 0
              }} 
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Collapsible Section Component
function CollapsibleSection({ title, icon, children, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          bgcolor: expanded ? 'background.paper' : 'action.hover',
          borderRadius: 1,
          transition: 'background-color 0.2s'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon}
          <Typography variant={isMobile ? "subtitle2" : "subtitle1"} sx={{ ml: 1, fontWeight: 'medium' }}>
            {title}
          </Typography>
        </Box>
        {isMobile && (expanded ? <ExpandLess /> : <ExpandMore />)}
      </Box>
      <Collapse in={!isMobile || expanded}>
        <Box sx={{ p: 2, pt: 1 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

function InvoicesPaymentsPanel({ onNotification }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState({ invoices: true, paymentMethods: true });
  const [error, setError] = useState({ invoices: null, paymentMethods: null });
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Fetch invoices and payment methods
    const fetchBillingData = async () => {
      // Get invoices
      try {
        setLoading(prev => ({ ...prev, invoices: true }));
        setError(prev => ({ ...prev, invoices: null }));

        const response = await axios.get('/api/billing/invoices');

        if (response.data.status === 'success') {
          setInvoices(response.data.data?.data || []);
        } else {
          throw new Error(response.data.message || 'Failed to fetch invoices');
        }
      } catch (err) {
        console.error('Error fetching invoices:', err);
        setError(prev => ({
          ...prev,
          invoices: err.response?.data?.message || err.message || 'Failed to load invoices'
        }));
        // Don't show user notification for every invoice error - this can be noisy
        // Just display the error in the UI
      } finally {
        setLoading(prev => ({ ...prev, invoices: false }));
      }

      // Get payment methods
      try {
        setLoading(prev => ({ ...prev, paymentMethods: true }));
        setError(prev => ({ ...prev, paymentMethods: null }));

        const response = await axios.get('/api/billing/payment-methods');

        if (response.data.status === 'success') {
          setPaymentMethods(response.data.data?.paymentMethods || []);
        } else {
          throw new Error(response.data.message || 'Failed to fetch payment methods');
        }
      } catch (err) {
        console.error('Error fetching payment methods:', err);
        setError(prev => ({
          ...prev,
          paymentMethods: err.response?.data?.message || err.message || 'Failed to load payment methods'
        }));
        // Don't show notification for every error
      } finally {
        setLoading(prev => ({ ...prev, paymentMethods: false }));
      }
    };

    fetchBillingData();
  }, []);

  const handleManagePayments = async () => {
    try {
      setPortalLoading(true);

      const response = await axios.post('/api/billing/create-customer-portal-session', {
        user: user,
        origin: 'dashboard#invoices'
      });

      if (response.data.status === 'success' && response.data.data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = response.data.data.url;
      } else {
        throw new Error(response.data.message || 'Failed to create portal session');
      }
    } catch (err) {
      console.error('Error creating portal session:', err);
      onNotification(
        'Error',
        err.response?.data?.message || err.message || 'Failed to open payment portal',
        'error'
      );
    } finally {
      setPortalLoading(false);
    }
  };

  const handleTierChange = async (newTier) => {
    // Refresh the page after tier change to update all billing related components
    console.log('newTier', newTier);
    window.location.reload();
  };

  return (
    <Box>
      {/* Invoice Management Section */}
      <Paper sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center',
          fontSize: isMobile ? '1.1rem' : '1.25rem'
        }}>
          <Receipt sx={{ mr: 1 }} /> Invoice History
        </Typography>

        {loading.invoices ? (
          <LoadingState message="Loading invoices..." />
        ) : error.invoices ? (
          <ErrorState
            error={error.invoices}
            variant="alert"
          />
        ) : invoices.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No invoices found. They will appear here once generated.
            </Typography>
          </Box>
        ) : (
          <List sx={{ 
            width: '100%', 
            bgcolor: 'background.paper',
            p: 0
          }}>
            {invoices.map(invoice => (
              <InvoiceListItem 
                key={invoice.id} 
                invoice={invoice} 
                isMobile={isMobile}
              />
            ))}
          </List>
        )}
      </Paper>

      {/* Payment Methods Section */}
      <Paper sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'flex-start',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}>
          <Typography variant="h6" gutterBottom={isMobile} sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: isMobile ? '1.1rem' : '1.25rem'
          }}>
            <CreditCard sx={{ mr: 1 }} /> Payment Methods
          </Typography>

          <Button
            variant="contained"
            onClick={handleManagePayments}
            disabled={portalLoading}
            startIcon={portalLoading ? <CircularProgress size={20} /> : <OpenInNew />}
            fullWidth={isMobile}
          >
            Manage Payment Methods
          </Button>
        </Box>

        {loading.paymentMethods ? (
          <LoadingState message="Loading payment methods..." />
        ) : error.paymentMethods ? (
          <ErrorState
            error={error.paymentMethods}
            variant="alert"
          />
        ) : paymentMethods.length === 0 ? (
          <Box sx={{ 
            p: 2, 
            textAlign: 'center', 
            border: '1px dashed', 
            borderColor: 'divider', 
            borderRadius: 1 
          }}>
            <Typography color="text.secondary" gutterBottom>
              No payment methods found.
            </Typography>
            <Button
              variant="outlined"
              onClick={handleManagePayments}
              disabled={portalLoading}
              startIcon={portalLoading ? <CircularProgress size={20} /> : null}
              sx={{ mt: 1 }}
              fullWidth={isMobile}
            >
              Add Payment Method
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {paymentMethods.map(paymentMethod => (
              <PaymentMethodCard 
                key={paymentMethod.id} 
                paymentMethod={paymentMethod}
                isMobile={isMobile}
              />
            ))}
          </Stack>
        )}
      </Paper>

      {/* Billing Settings Section */}
      <Paper sx={{ p: isMobile ? 2 : 3 }}>
        <Typography variant="h6" gutterBottom sx={{ 
          display: 'flex', 
          mb: 3, 
          alignItems: 'center',
          fontSize: isMobile ? '1.1rem' : '1.25rem' 
        }}>
          <Settings sx={{ mr: 1 }} /> Billing Settings
        </Typography>

        <Stack spacing={isMobile ? 2 : 3}>
          {/* Service Plan Selection */}
          <CollapsibleSection 
            title="Service Plan" 
            icon={<CreditCard sx={{ mr: 1 }} />}
            defaultExpanded={true}
          >
            <ServiceTierSelect
              currentTier={user?.serviceTier || 'free'}
              onTierChange={handleTierChange}
              disabled={portalLoading}
            />
          </CollapsibleSection>

          {/* Billing Information */}
          <CollapsibleSection 
            title="Billing Information" 
            icon={<Receipt sx={{ mr: 1 }} />}
            defaultExpanded={!isMobile}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant={isMobile ? "body2" : "body1"}>
                  {user?.email || 'Not set'}
                </Typography>
              </Box>
              
              {user?.company && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Company Name
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "body1"}>
                    {user.company}
                  </Typography>
                </Box>
              )}

              {user?.vatNumber && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    VAT Number
                  </Typography>
                  <Typography variant={isMobile ? "body2" : "body1"}>
                    {user.vatNumber}
                  </Typography>
                </Box>
              )}

              {user?.billingAddress && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Billing Address
                  </Typography>
                  <Typography 
                    variant={isMobile ? "body2" : "body1"} 
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {user.billingAddress}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CollapsibleSection>

          {/* Billing Period */}
          <CollapsibleSection 
            title="Billing Period" 
            icon={<AccessTime sx={{ mr: 1 }} />}
            defaultExpanded={!isMobile}
          >
            <Typography variant={isMobile ? "body2" : "body1"}>
              Monthly
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mt: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
            >
              You are billed for your base plan and any usage overages on a monthly basis.
            </Typography>
          </CollapsibleSection>
        </Stack>
      </Paper>
    </Box>
  );
}

export default InvoicesPaymentsPanel; 